import React, { useRef, useState, useCallback, useEffect } from "react";
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Radio,
    Button,
    Checkbox,
    Collapse,
} from "antd";
import { Avatar, message, ConfigProvider, theme as antdTheme, Upload } from "antd";
import type { UploadFile } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocationData } from "../../hooks/useLocationData";
import { filterOptionVietnamese } from "../../lib/utils";
import { extractApiError } from "../../lib/apiError";
import { CameraOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { mixGetV1, mixPostV2 } from "../../services/mixin";
import useAuthStore from "../../store/authStore";
const { TextArea } = Input;

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit?: (values: any) => void;
    /** Điền sẵn số điện thoại khi mở (VD: tạo khách từ cuộc gọi số lạ) */
    initialPhone?: string;
}

/** Bỏ khoảng trắng / dấu chấm / gạch ngang người dùng hay gõ trong SĐT */
const cleanPhone = (v?: string) => (v || "").replace(/[\s.\-()]/g, "");
const PHONE_RE = /^\+?\d{8,15}$/;
/** "nguyễn  thị lan" → "Nguyễn Thị Lan" */
const titleCase = (v?: string) => (v || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("vi").replace(/(^|\s)(\S)/g, (_, s, c) => s + c.toLocaleUpperCase("vi"));

interface DupCustomer { id: number; name: string; code: string }

/** Tiêu đề nhóm trường */
const Section: React.FC<{ title: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode }> = ({ title, extra, children }) => (
    <section className="mb-5 last:mb-0">
        <div className="mb-3 flex items-center justify-between gap-2 border-b border-gray-100 pb-1.5 dark:border-[#303030]">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</span>
            {extra}
        </div>
        {children}
    </section>
);

const CreateCustomerModal: React.FC<Props> = ({
    open,
    onClose,
    onSubmit,
    initialPhone,
}) => {
    const [form] = Form.useForm();
    const { t, i18n } = useTranslation();
    const { provinceOptions, wardOptions, loadWards, setWardOptions } = useLocationData();
    const isDark = document.documentElement.classList.contains("dark");

    // "Người tạo" (staff_id): tài khoản không phải super admin -> tự gán chính mình và KHÔNG được đổi.
    // Cấu trúc user: tài khoản nhân viên thì user.info CHÍNH LÀ bản ghi staff (id staff = info.id);
    // tài khoản admin thì info là bản ghi user -> chỉ tin info.staff_id.
    const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserType = Number(storedUser?.type ?? storedUser?.info?.type ?? 0);
    const currentStaffId = currentUserType === 1
        ? (Number(storedUser?.info?.staff_id ?? 0) || null)
        : (Number(storedUser?.info?.staff_id ?? storedUser?.info?.id ?? 0) || null);

    // Dropdown options
    const [occupations, setOccupations] = useState<{ label: string; value: number }[]>([]);
    const [careStatuses, setCareStatuses] = useState<{ label: string; value: number; color: string}[]>([]);
    const [staff, setStaff] = useState<{ label: string; value: number }[]>([]);
    const [telesales, setTelesales] = useState<{ label: string; value: number }[]>([]);
    const [channelConnection, setChannelConnection] = useState<{ label: string; value: number }[]>([]);

    // Fetch dropdown options
    const fetchOptions = useCallback(async () => {
        try {
            const [careRes, occRes, staffRes, telesaleRes, providerRes] = await Promise.all([
                mixGetV1('/api/v1/be/customer-care-statuses', { limit: 100, sort: ["id,desc"] }).catch(() => null),
                mixGetV1('/api/v1/be/customer-occupations', { take: 100 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 2 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 3 }).catch(() => null),
                mixGetV1('/api/v1/be/oauth-accounts', { limit: 100, sort: ["id,desc"] }).catch(() => null),
            ]);

            if (careRes?.status && careRes.data?.items) {
                setCareStatuses(careRes.data.items.map((s: any) => ({ label: s.name, value: s.id, color: s.color })));
            }
            if (occRes?.status && occRes.data?.items) {
                setOccupations(occRes.data.items.map((o: any) => ({ label: o.name, value: o.id })));
            }
            if (staffRes?.status && staffRes.data?.items) {
                setStaff(staffRes.data.items.map((u: any) => ({ label: u.name, value: u.id })));
            }
            if (telesaleRes?.status && telesaleRes.data?.items) {
                setTelesales(telesaleRes.data.items.map((u: any) => ({ label: u.name, value: u.id })));
            }
            if (providerRes?.status && providerRes.data?.items) {
                const Channelconnections = providerRes.data.items.flatMap((item: any) =>
                    (item.connections || []).map((conn: any) => ({
                        label: conn.display_name,
                        value: conn.id,
                    }))
                );
                setChannelConnection(Channelconnections)
            }
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    }, []);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    // Mở modal: tài khoản không phải super admin -> tự gán "Người tạo" là chính mình
    useEffect(() => {
        if (open && !isSuperAdmin && currentStaffId) {
            form.setFieldsValue({ staff_id: currentStaffId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isSuperAdmin, currentStaffId]);
    // Mở từ cuộc gọi số lạ (Tổng đài Sales) -> điền sẵn số gọi đến
    useEffect(() => {
        if (open && initialPhone) form.setFieldsValue({ phone: initialPhone });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialPhone]);

    // Đảm bảo option của chính mình luôn hiển thị tên (kể cả khi không nằm trong danh sách trực page)
    const creatorOptions = (!isSuperAdmin && currentStaffId && !staff.some((s) => s.value === currentStaffId))
        ? [{ label: storedUser?.info?.name || storedUser?.fullname || `#${currentStaffId}`, value: currentStaffId }, ...staff]
        : staff;

    // Avatar
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const displayName: string | undefined = Form.useWatch("display_name", form);
    const fullname: string | undefined = Form.useWatch("fullname", form);
    // Họ tên thật nhập chuẩn: bỏ khoảng trắng thừa, viết hoa chữ đầu mỗi tiếng; nhắc khi gõ không dấu (họp 30/10)
    const noAccent = !!fullname?.trim() && !/[À-ỹĐđ]/.test(fullname);

    // Hình ảnh đính kèm khi tạo khách (param "photos" — array files, tối đa 10MB/ảnh, chọn được nhiều ảnh)
    const [photoList, setPhotoList] = useState<UploadFile[]>([]);
    const beforeAddPhoto = (file: File) => {
        if (!file.type.startsWith("image/")) {
            message.error(t("customer.photo_invalid_type"));
            return Upload.LIST_IGNORE;
        }
        if (file.size > 10 * 1024 * 1024) {
            message.error(t("customer.photo_too_large", { name: file.name }));
            return Upload.LIST_IGNORE;
        }
        return false; // giữ file trong danh sách, không tự upload — gửi kèm khi bấm Tạo
    };

    const [submitting, setSubmitting] = useState<"save" | "next" | null>(null);
    // Bấm "Tạo & nhập tiếp" thì giữ nguồn / phân công để nhập khách kế tiếp nhanh hơn
    const keepGoing = useRef(false);

    // SĐT đã có khách: API tạo khách sẽ cập nhật vào hồ sơ cũ nên cảnh báo trước khi bấm Tạo
    const phone: string | undefined = Form.useWatch("phone", form);
    const [dup, setDup] = useState<DupCustomer | null>(null);
    useEffect(() => {
        const p = cleanPhone(phone);
        setDup(null);
        if (!open || !PHONE_RE.test(p)) return;
        let alive = true;
        const timer = setTimeout(async () => {
            const res = await mixGetV1('/api/v1/be/customers', { search: p, limit: 5 }).catch(() => null);
            const tail = p.replace(/^\+?84|^0/, "");
            const hit = (res?.data?.items || []).find((c: any) => cleanPhone(c.phone).endsWith(tail));
            if (alive && hit) setDup({ id: hit.id, name: hit.display_name || hit.fullname || `KH-${hit.id}`, code: hit.customer_code || `KH-${hit.id}` });
        }, 450);
        return () => { alive = false; clearTimeout(timer); };
    }, [phone, open]);

    const resetAll = (keep?: Record<string, any>) => {
        form.resetFields();
        if (keep) form.setFieldsValue(keep);
        else if (!isSuperAdmin && currentStaffId) form.setFieldsValue({ staff_id: currentStaffId });
        setPhotoList([]);
        setWardOptions([]);
        setAvatarFile(null);
        if (preview) URL.revokeObjectURL(preview);
        setPreview(null);
        setDup(null);
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const handleSave = async (values: any): Promise<boolean> => {

        const { channel_connection_id, ...rest } = values;

       // Hình ảnh đính kèm: mixPostV2 tự chuyển mảng thành photos[] trong FormData
       const photoFiles = photoList.map((f) => f.originFileObj).filter(Boolean) as File[];

       const payload = {
            ...rest,
            avatar: avatarFile || null,
            photos: photoFiles.length > 0 ? photoFiles : undefined,
            channel_connection_ids: channel_connection_id
                ? [channel_connection_id]
                : [],
        };

        const res = await mixPostV2('/api/v1/be/customers', payload);

        if (res.status) {
            message.success(t("customer.create_success"));
            return true;
        } else {
            // Ưu tiên hiển thị đúng lỗi API trả về (đã map sang tiếng Việt), nếu không có thì dùng thông báo mặc định
            const apiErr = extractApiError(res, i18n.language);
            message.error(apiErr || t("customer.create_error"));
            return false;
        }
    }

    const handleFinish = async (values: any) => {
        const keep = keepGoing.current;
        keepGoing.current = false;
        const data = { ...values };

        if (data.birthday) {
            data.birthday = data.birthday.format('YYYY-MM-DD');
        }
        if (data.phone) data.phone = cleanPhone(data.phone);

        // API nhận is_foreign dạng số: 0 = trong nước, 1 = nước ngoài
        data.is_foreign = data.is_foreign ? 1 : 0;

        setSubmitting(keep ? "next" : "save");
        const success = await handleSave(data).finally(() => setSubmitting(null));

        if (!success) return; // ❗ chặn nếu fail

        onSubmit?.(data);
        if (keep) {
            resetAll({
                staff_id: values.staff_id,
                telesale_id: values.telesale_id,
                channel_connection_id: values.channel_connection_id,
                customer_care_status_id: values.customer_care_status_id,
            });
            form.getFieldInstance("display_name")?.focus?.();
            return;
        }
        resetAll();
        onClose();
    };

    const handleProvinceChange = (value: string) => {
        form.setFieldsValue({ ward_code: undefined });
        setWardOptions([]);
        if (value) loadWards(value);
    };

    const titleOptions = [
        { value: "mr", label: "Mr." },
        { value: "ms", label: "Ms." },
        { value: "mrs", label: "Mrs." },
    ];

    const genderGroup = (values: [any, any, any]) => (
        <Radio.Group optionType="button" buttonStyle="solid" className="flex [&_.ant-radio-button-wrapper]:flex-1 [&_.ant-radio-button-wrapper]:text-center">
            <Radio.Button value={values[0]}>{t('customer.male')}</Radio.Button>
            <Radio.Button value={values[1]}>{t('customer.female')}</Radio.Button>
            <Radio.Button value={values[2]}>{t('customer.other_gender')}</Radio.Button>
        </Radio.Group>
    );

    return (
        <ConfigProvider
            theme={{
                algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: {
                    colorPrimary: "#3e91ab", // [#3e91ab]
                },
                components: {
                    Form: { itemMarginBottom: 14, verticalLabelPadding: "0 0 4px" },
                },
            }}
        >
            <Modal
                title={
                    <div>
                        <div className="text-[18px] font-semibold t-text-white">{t('customer.create_customer')}</div>
                        <div className="text-[12.5px] font-normal text-gray-500 dark:text-gray-400">{t('customer.create_hint')}</div>
                    </div>
                }
                open={open}
                onCancel={handleClose}
                width={980}
                centered
                destroyOnClose={false}
                maskClosable={false}
                styles={{
                    body: { maxHeight: "calc(100vh - 210px)", overflowY: "auto", overflowX: "hidden", margin: "0 -24px", padding: "4px 24px 0" },
                }}
                footer={
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button onClick={handleClose} disabled={!!submitting}>
                            {t('customer.cancel')}
                        </Button>
                        <Button
                            loading={submitting === "next"}
                            disabled={submitting === "save"}
                            onClick={() => { keepGoing.current = true; form.submit(); }}
                        >
                            {t('customer.create_and_next')}
                        </Button>
                        <Button
                            type="primary"
                            loading={submitting === "save"}
                            disabled={submitting === "next"}
                            onClick={() => { keepGoing.current = false; form.submit(); }}
                            className="!bg-[#3e91ab] font-semibold"
                        >
                            {t('customer.create')}
                        </Button>
                    </div>
                }
                className="
                    dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                    dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                    dark:[&_.ant-modal-title]:text-white
                    dark:[&_.ant-modal-close]:text-gray-300
                "
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    onFinishFailed={({ errorFields }) => { keepGoing.current = false; form.scrollToField(errorFields[0]?.name, { block: "center", behavior: "smooth" }); }}
                    requiredMark
                >
                    <div className="grid grid-cols-1 gap-x-6 md:grid-cols-3">

                        {/* ================= CỘT TRÁI: hồ sơ khách ================= */}
                        <div className="md:col-span-2">
                            <Section title={t('customer.customer_details')}>
                                <div className="flex gap-4">
                                    {/* Ảnh đại diện: bấm vào vòng tròn để chọn ảnh */}
                                    <div className="shrink-0 pt-[26px]">
                                        <button
                                            type="button"
                                            className="group relative block h-[72px] w-[72px] rounded-full"
                                            onClick={() => fileInputRef.current?.click()}
                                            title={t('customer.customer_avatar')}
                                            aria-label={t('customer.customer_avatar')}
                                        >
                                            <Avatar
                                                size={72}
                                                src={preview || undefined}
                                                className="border border-slate-200 bg-[#3e91ab]/15 text-[26px] font-semibold text-[#3e91ab] dark:border-slate-700"
                                            >
                                                {displayName?.trim().charAt(0).toUpperCase() || <CameraOutlined className="text-[22px]" />}
                                            </Avatar>
                                            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                                                <CameraOutlined className="text-[20px] text-white" />
                                            </span>
                                        </button>
                                        {preview && (
                                            <button
                                                type="button"
                                                className="mx-auto mt-1 block text-[11.5px] text-gray-400 hover:text-red-500"
                                                onClick={() => { URL.revokeObjectURL(preview); setPreview(null); setAvatarFile(null); }}
                                            >
                                                <CloseOutlined className="mr-0.5 text-[10px]" />{t('customer.remove_avatar')}
                                            </button>
                                        )}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                e.target.value = "";
                                                if (!file) return;
                                                if (file.size > 5 * 1024 * 1024) {
                                                    message.error(t('customer.avatar_too_large'));
                                                    return;
                                                }
                                                if (preview) URL.revokeObjectURL(preview);
                                                setAvatarFile(file);
                                                setPreview(URL.createObjectURL(file));
                                            }}
                                        />
                                    </div>

                                    <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 sm:grid-cols-2">
                                        <Form.Item
                                            label={t('customer.customer_name')}
                                            name="display_name"
                                            rules={[{ required: true, whitespace: true, message: t('customer.required') }]}
                                        >
                                            <Input autoFocus placeholder={t('customer.customer_name_placeholder')} maxLength={150} />
                                        </Form.Item>

                                        <Form.Item
                                            label={t('customer.phone')}
                                            name="phone"
                                            rules={[{
                                                validator: (_, v) => (!v || PHONE_RE.test(cleanPhone(v)) ? Promise.resolve() : Promise.reject(new Error(t('customer.phone_invalid')))),
                                            }]}
                                            validateStatus={dup ? "warning" : undefined}
                                            help={dup ? (
                                                <span>
                                                    {t('customer.phone_exists')}{" "}
                                                    <Link to={`/customer/${dup.id}`} target="_blank" className="font-medium !text-[#3e91ab]">{dup.name} · {dup.code}</Link>
                                                </span>
                                            ) : undefined}
                                        >
                                            <Input inputMode="tel" placeholder="09xx xxx xxx" allowClear />
                                        </Form.Item>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                                    {/* Họ tên thật của khách (display_name là tên hiển thị) */}
                                    <Form.Item label={t('customer.customer_fullname')} name="fullname"
                                        validateStatus={noAccent ? "warning" : undefined}
                                        help={noAccent ? "Tên đang không dấu — nhập đúng họ tên tiếng Việt có dấu (VD: Nguyễn Thị Lan)" : undefined}>
                                        <Input placeholder="Nguyễn Thị Lan" onBlur={(e) => form.setFieldValue("fullname", titleCase(e.target.value))} />
                                    </Form.Item>

                                    <Form.Item label={t('customer.gender')} name="gender">
                                        {/* Quy ước chung với API: 0 chưa rõ, 1 Nam, 2 Nữ, 3 Khác. Không chọn thì không gửi */}
                                        {genderGroup([1, 2, 3])}
                                    </Form.Item>

                                    <Form.Item label={t('customer.dob')} name="birthday">
                                        <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="DD/MM/YYYY" />
                                    </Form.Item>

                                    <Form.Item label="Email" name="email" rules={[{ type: "email", message: t('customer.email_invalid') }]}>
                                        <Input placeholder="email@example.com" />
                                    </Form.Item>

                                    <Form.Item label={t('customer.career')} name="customer_occupation_id" className="sm:col-span-2">
                                        <Select
                                            placeholder={t('customer.select_career')}
                                            options={occupations}
                                            showSearch
                                            optionFilterProp="label"
                                            allowClear
                                        />
                                    </Form.Item>
                                </div>
                            </Section>

                            <Section
                                title={t('customer.address')}
                                extra={
                                    // Khách nước ngoài (is_foreign): chỉ đánh dấu nguồn gốc khách, Tỉnh/TP - Phường/Xã vẫn nhập bình thường vì không bắt buộc
                                    <Form.Item name="is_foreign" valuePropName="checked" noStyle>
                                        <Checkbox className="text-[13px]">{t('customer.foreign_customer')}</Checkbox>
                                    </Form.Item>
                                }
                            >
                                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                                    <Form.Item label={t('customer.province')} name="province_code">
                                        <Select
                                            placeholder={t('customer.select_province')}
                                            options={provinceOptions}
                                            onChange={handleProvinceChange}
                                            showSearch
                                            filterOption={filterOptionVietnamese}
                                            allowClear
                                        />
                                    </Form.Item>

                                    <Form.Item label={t('customer.ward')} name="ward_code">
                                        <Select
                                            placeholder={t('customer.select_ward')}
                                            options={wardOptions}
                                            showSearch
                                            filterOption={filterOptionVietnamese}
                                            allowClear
                                        />
                                    </Form.Item>

                                    <Form.Item label={t('customer.detail_address')} name="address" className="sm:col-span-2">
                                        <Input placeholder={t('customer.address_placeholder')} />
                                    </Form.Item>
                                </div>
                            </Section>

                            <Section title={t('customer.notes')}>
                                <Form.Item name="note">
                                    <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder={t('customer.enter_notes')} />
                                </Form.Item>

                                {/* Hình ảnh đính kèm (photos[]) — chọn nhiều ảnh cùng lúc, tối đa 10MB/ảnh */}
                                <Form.Item label={`${t('customer.photos')} (${t('customer.photos_hint')})`}>
                                    <Upload
                                        listType="picture-card"
                                        multiple
                                        accept="image/*"
                                        fileList={photoList}
                                        beforeUpload={beforeAddPhoto}
                                        onChange={({ fileList }) => setPhotoList(fileList)}
                                    >
                                        <div>
                                            <PlusOutlined />
                                            <div className="mt-1 text-xs">{t('customer.add_photo')}</div>
                                        </div>
                                    </Upload>
                                </Form.Item>
                            </Section>
                        </div>

                        {/* ================= CỘT PHẢI: nguồn, phân công, người liên hệ ================= */}
                        {/* Ghim cả cột (phân công + người liên hệ) khi cuộn form bên trái */}
                        <div className="md:col-span-1 md:sticky md:top-0 md:self-start">
                            <div className="rounded-xl bg-gray-50 p-4 dark:bg-[#262626]">
                                <Section title={t('customer.source_assign')}>
                                    <Form.Item label={t('customer.source')} name="channel_connection_id">
                                        <Select
                                            placeholder={t('customer.select_source')}
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            options={channelConnection}
                                        />
                                    </Form.Item>

                                    <Form.Item label={t('customer.care_status')} name="customer_care_status_id">
                                        <Select
                                            placeholder={t('customer.select_care_status')}
                                            allowClear
                                            showSearch
                                            optionFilterProp="title"
                                            options={careStatuses.map((item) => ({
                                                value: item.value,
                                                title: item.label,
                                                label: (
                                                    <span className="inline-flex items-center gap-2">
                                                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color || "#9ca3af" }} />
                                                        {item.label}
                                                    </span>
                                                ),
                                            }))}
                                        />
                                    </Form.Item>

                                    {/* Người tạo: chỉ super admin được đổi; nhân viên (vd trực page) tự gán chính mình */}
                                    <Form.Item
                                        label={t('customer.creator')}
                                        name="staff_id"
                                        rules={[{ required: true, message: t('customer.creator_required') }]}
                                    >
                                        <Select
                                            placeholder={t('customer.select_creator')}
                                            options={creatorOptions}
                                            disabled={!isSuperAdmin}
                                            showSearch
                                            optionFilterProp="label"
                                        />
                                    </Form.Item>

                                    {/* Phân công Telesale — gửi telesale_id (alias contact_staff_id) theo tài liệu API tạo khách hàng */}
                                    <Form.Item
                                        label={t('customer.assign_telesale')}
                                        name="telesale_id"
                                        rules={[{ required: true, message: t('customer.telesale_required') }]}
                                        className="!mb-0"
                                    >
                                        <Select
                                            placeholder={t('customer.select_telesale')}
                                            options={telesales}
                                            showSearch
                                            optionFilterProp="label"
                                            allowClear
                                        />
                                    </Form.Item>
                                </Section>
                            </div>

                            {/* Người liên hệ (khách doanh nghiệp / người nhà) — ít dùng nên thu gọn */}
                            <Collapse
                                ghost
                                size="small"
                                className="mt-3 [&_.ant-collapse-content-box]:!px-1 [&_.ant-collapse-header]:!px-1"
                                items={[{
                                    key: "contact",
                                    forceRender: true,
                                    label: (
                                        <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            {t('customer.contact_info')} <span className="font-normal normal-case tracking-normal text-gray-400">· {t('customer.optional')}</span>
                                        </span>
                                    ),
                                    children: (
                                        <>
                                            <Form.Item label={t('customer.full_name')} name="contactName">
                                                <Input placeholder={t('customer.enter_full_name')} />
                                            </Form.Item>
                                            <div className="grid grid-cols-2 gap-x-3">
                                                <Form.Item label={t('customer.title_position')} name="contactTitle">
                                                    <Select placeholder={t('customer.select_title')} options={titleOptions} allowClear />
                                                </Form.Item>
                                                <Form.Item label={t('customer.position')} name="contactPosition">
                                                    <Input placeholder={t('customer.enter_position')} />
                                                </Form.Item>
                                            </div>
                                            <Form.Item label={t('customer.phone')} name="contactPhone">
                                                <Input inputMode="tel" placeholder={t('customer.enter_phone')} />
                                            </Form.Item>
                                            <Form.Item label={t('customer.email')} name="contactEmail">
                                                <Input placeholder={t('customer.enter_email')} />
                                            </Form.Item>
                                            <Form.Item label={t('customer.gender')} name="contactGender" className="!mb-0">
                                                {genderGroup(["male", "female", "other"])}
                                            </Form.Item>
                                        </>
                                    ),
                                }]}
                            />
                        </div>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default CreateCustomerModal;
